import { useMemo } from 'react';
import type {
  Worksheet,
  SectionType,
  ContextGroup,
  MeasurementGroup,
  FindingGroup,
  FlagGroup,
  FollowUpGroup,
} from '@/models/template';
import { EditableContextSection } from './sections/EditableContextSection';
import { EditableMeasurementsSection } from './sections/EditableMeasurementsSection';
import { EditableFindingsSection } from './sections/EditableFindingsSection';
import { EditableFollowUpSection } from './sections/EditableFollowUpSection';
import { EditableConclusionsSection } from './sections/EditableConclusionsSection';
import { EditableTagsSection } from './sections/EditableTagsSection';
import { intToRoman } from './primitives/format';
import styles from './WorksheetCanvas.module.css';

const DEFAULT_ORDER: SectionType[] = [
  'CONTEXT',
  'MEASUREMENTS',
  'FINDINGS',
  'FOLLOW_UP',
  'CONCLUSION',
];

const SECTION_TITLES: Record<SectionType, string> = {
  CONTEXT: 'Context',
  MEASUREMENTS: 'Measurements',
  FINDINGS: 'Findings',
  FOLLOW_UP: 'Follow Up',
  CONCLUSION: 'Conclusions',
  TAGS: 'Tags',
  FLAGS: 'Flags',
  SIGNATURES: 'Signatures',
};

interface WorksheetCanvasProps {
  worksheet: Worksheet;
  onContextGroupsChange: (groups: ContextGroup[]) => void;
  onMeasurementGroupsChange: (groups: MeasurementGroup[]) => void;
  onFindingGroupsChange: (groups: FindingGroup[]) => void;
  onFlagGroupsChange: (groups: FlagGroup[]) => void;
  onFollowUpGroupsChange: (groups: FollowUpGroup[]) => void;
}

export function WorksheetCanvas({
  worksheet,
  onContextGroupsChange,
  onMeasurementGroupsChange,
  onFindingGroupsChange,
  onFlagGroupsChange,
  onFollowUpGroupsChange,
}: WorksheetCanvasProps) {
  const sectionOrder = useMemo<SectionType[]>(() => {
    if (!worksheet.sections || worksheet.sections.length === 0) {
      return DEFAULT_ORDER;
    }
    return [...worksheet.sections]
      .sort((a, b) => a.index - b.index)
      .map((s) => s.sectionType);
  }, [worksheet.sections]);

  const renderSection = (type: SectionType, position: number) => {
    const title = `${intToRoman(position)}. ${SECTION_TITLES[type]}`;
    switch (type) {
      case 'CONTEXT':
        return (
          <EditableContextSection
            key="context"
            title={title}
            groups={worksheet.contextGroups}
            onChange={onContextGroupsChange}
          />
        );
      case 'MEASUREMENTS':
        return (
          <EditableMeasurementsSection
            key="measurements"
            title={title}
            groups={worksheet.measurementGroups}
            onChange={onMeasurementGroupsChange}
          />
        );
      case 'FINDINGS':
        return (
          <EditableFindingsSection
            key="findings"
            title={title}
            findingGroups={worksheet.findingGroups}
            flagGroups={worksheet.flagGroups}
            onFindingGroupsChange={onFindingGroupsChange}
            onFlagGroupsChange={onFlagGroupsChange}
          />
        );
      case 'FOLLOW_UP':
        return (
          <EditableFollowUpSection
            key="followUp"
            title={title}
            groups={worksheet.followUpGroups ?? []}
            onChange={onFollowUpGroupsChange}
          />
        );
      case 'CONCLUSION':
        return <EditableConclusionsSection key="conclusion" title={title} />;
      case 'TAGS':
        return <EditableTagsSection key="tags" title={title} />;
      case 'FLAGS':
      case 'SIGNATURES':
        return null;
      default:
        return null;
    }
  };

  return (
    <div className={styles.canvas}>
      {sectionOrder.map((type, idx) => renderSection(type, idx + 1))}
    </div>
  );
}
